/* eslint-disable react/no-unstable-nested-components */
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import PremiumPaywallPopover from "components/conversion/PremiumPaywallPopover";
import { PrimitiveSelectItem, Select, SelectContent, SelectTrigger } from "components/ui/Select";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import { DownloadIcon } from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useIntl } from "react-intl";

interface SelectStatementTypeProps {
  disabled?: boolean;
  onSelectExportFormat?: (format: "xlsx" | "csv", turnstileToken: string) => void;
}
export default function SelectExportFormat({ disabled = false, onSelectExportFormat }: SelectStatementTypeProps) {
  const intl = useIntl();
  const theme = useTheme();
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);
  const [pendingFormat, setPendingFormat] = useState<"xlsx" | "csv" | null>(null);
  const pendingFormatRef = useRef<"xlsx" | "csv" | null>(null);

  const AVAILABLE_FORMATS = [
    {
      value: "csv",
      label: intl.formatMessage({ defaultMessage: "CSV", id: "8UzT6d" }),
    },
    {
      value: "xlsx",
      label: intl.formatMessage({ defaultMessage: "Excel", id: "vwxVnf" }),
    },
  ];

  if (disabled) {
    return (
      <PremiumPaywallPopover
        isEnabled={disabled}
        displayOrigin="Export financials data"
        title={intl.formatMessage({ defaultMessage: "Só no Premium Anual...", id: "LmUAaj" })}
        render={(a) => {
          return intl.formatMessage(
            {
              defaultMessage: "<a>Clique aqui</a> e junte-se ao time para poder fazer download dos dados!",
              id: "Rw7AER",
            },
            {
              a,
            },
          );
        }}
      >
        <button
          type="button"
          disabled={disabled}
          className={classNames(
            "relative flex min-h-10 min-w-10 items-center justify-center rounded-xl border border-slate-300 bg-white p-2",
            "max-w-10",
            "cursor-pointer",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "dark:border-slate-950 dark:bg-slate-700 dark:opacity-30",
          )}
        >
          <DownloadIcon strokeWidth={2.25} absoluteStrokeWidth={true} className="size-4" />
        </button>
      </PremiumPaywallPopover>
    );
  }

  function handleTurnstileFailure() {
    pendingFormatRef.current = null;
    setPendingFormat(null);
    turnstileRef.current?.reset();
    toast.error(intl.formatMessage({ defaultMessage: "Erro ao validar exportação", id: "t2WOJt" }));
  }

  return (
    <>
      <Turnstile
        ref={turnstileRef}
        siteKey={process.env.TURNSTILE_SITE_KEY || ""}
        // className="hidden"
        onSuccess={(token) => {
          if (pendingFormatRef.current) {
            onSelectExportFormat?.(pendingFormatRef.current, token);
          }

          pendingFormatRef.current = null;
          setPendingFormat(null);
          turnstileRef.current?.reset();
        }}
        onError={() => handleTurnstileFailure()}
        onExpire={() => handleTurnstileFailure()}
        onUnsupported={() => handleTurnstileFailure()}
        style={{
          position: "absolute",
          top: -24,
          left: -24,
          width: 0,
          height: 0,
          overflow: "hidden",
        }}
        options={{
          theme: "auto",
          language: "auto",
          appearance: "execute",
          refreshExpired: "auto",
          refreshTimeout: "auto",
          execution: "execute",
          size: "invisible",
          retry: "auto",
          // retryInterval: 2000,
          feedbackEnabled: false,
          responseField: false,
        }}
      />

      <Select
        disabled={disabled || pendingFormat !== null}
        value=""
        onValueChange={(value) => {
          pendingFormatRef.current = value as "csv" | "xlsx";
          setPendingFormat(value as "csv" | "xlsx");
          turnstileRef.current?.execute();
        }}
      >
        <SelectTrigger
          hideChevronIcon={true}
          className={classNames(
            "relative flex min-h-10 min-w-10 max-w-10 items-center justify-center rounded-xl border border-slate-300 bg-white p-2",
            "hover:bg-slate-100",
            "aria-checked:cursor-pointer aria-checked:bg-slate-200",
            "group",
            "hover:border-emerald-400 hover:bg-emerald-50",
            "focus:outline-none",
            "disabled:pointer-events-none disabled:opacity-50",
            "dark:border-slate-950 dark:bg-slate-700",
            "dark:hover:bg-lime-400/30",
          )}
        >
          <DownloadIcon
            strokeWidth={2.25}
            absoluteStrokeWidth={true}
            className={classNames(
              "fill-gray-100 stroke-gray-700 group-hover:fill-emerald-100 group-hover:stroke-emerald-600",
              "dark:fill-transparent dark:stroke-slate-300 dark:group-hover:fill-lime-800 dark:group-hover:stroke-lime-300",
              "size-4",
            )}
          />
        </SelectTrigger>

        <SelectContent
          side="bottom"
          scrollAreaViewportClassName="space-y-1"
          className={classNames("min-w-28 rounded-2xl border border-slate-300 text-sm", {
            "border-slate-900 bg-slate-700 text-slate-300 shadow-neutral-950/40": theme === "dark",
            dark: theme === "dark",
          })}
        >
          {AVAILABLE_FORMATS.map(({ label, value }) => {
            return (
              <PrimitiveSelectItem
                key={label}
                value={value}
                className="data-[state=checked]:bg-slate-100 hover:data-[state=checked]:bg-slate-600/10 dark:data-[state=checked]:bg-slate-950"
              >
                <div className="flex w-full cursor-pointer items-center justify-between rounded-xl py-1 text-sm">
                  <h4 className="inline-flex text-xs dark:text-slate-300">{label}</h4>
                </div>
              </PrimitiveSelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </>
  );
}
